import { MapViewportUnitView } from '@mark43/cad-api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AgencyTypeGlobal } from '../../common/global-attributes';
import { areLatLngsEqual, isValidLatLng } from '../../location/helpers';
import { FeatureLayer, Graphic, MapView, Point, WebMap, reactiveUtils } from '../constructors';
import { createClusterContentInfoRow } from '../helpers/clusters';
import { EntityIdToObjectIdMaps } from '../helpers/entity-id-to-object-id-maps';
import { applyUnitIconTheme, createUnitsFeatureLayer } from '../helpers/feature-layers';
import { getUnitIconNameAndUiState, createUnitGraphic } from '../helpers/graphics';
import { getMapViewportUnitLocation, getUnitUserLocation } from '../helpers/locations';
import { agencyDefaultUnitIcon } from '../helpers/units';
import {
    getClusterPopupSelectedLabelColor,
    getMapIconTheme,
    getUnitStateId,
} from '../helpers/map-icon-theme';
import { getMapIconColorMode } from '../helpers/map-icon-color-mode';
import policeOnFootOnlineInactive from '../images/unit-icons/police/on-foot/unavailable/police_on_foot_unavailable_online_inactive.svg';
import type { CustomPopup, FeatureLayerView, MapMode } from '../types';
import { useLayerControl, usePreviousLayer } from './use-feature-layer-control';
import { EsriMapEntityTypeEnum } from '../types';
import { FEATURE_LAYERS_PROPERTIES } from '../constants';
import { useMapIconTheme } from './use-map-icon-theme';

type UnitFeatureLayerProps = {
    mapViewportUnits: MapViewportUnitView[];
    selectedUnitId?: number;
    selectUnit?: (unitId: number, shouldResetZoomLevel?: boolean) => void;
    selectedUnitUserId?: number;
    selectUnitUser?: (unitUserId: number) => void;
    selectedClusterId?: number;
    mapView?: MapView;
    webMap?: WebMap;
    showMobileDevicesOnMap?: boolean;
    enableUnitClusteringOnMap?: boolean;
    clusterRadius?: number;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
    showUnitLocationWhenGpsIsNotAvailable?: boolean;
    dispatchFromMap?: boolean;
    openUnitContextMenu: (unitId: number) => void;
    isFirstResponder?: boolean;
    mapMode: MapMode;
};

export const useUnitFeatureLayer = ({
    mapViewportUnits,
    selectedUnitId,
    selectedUnitUserId,
    selectUnit,
    selectUnitUser,
    mapView,
    webMap,
    selectedMapLedgerEntityType,
    showUnitLocationWhenGpsIsNotAvailable,
    showMobileDevicesOnMap = false,
    enableUnitClusteringOnMap = false,
    clusterRadius,
    dispatchFromMap,
    openUnitContextMenu,
    isFirstResponder,
    mapMode,
}: UnitFeatureLayerProps) => {
    const entityIdToObjectIdMapCacheRef = useRef<typeof EntityIdToObjectIdMaps.UNITS>(
        EntityIdToObjectIdMaps.UNITS
    );
    // `mapViewportUnits` is from mapStore, which only has Units.
    // `featureLayerUnitsAndUsersById` stores units and users that renderer on map.
    // (user graphics created for each `unitUserLocations`. User has exact same type as Unit `MapViewportUnitView`, with some fields overwritten)
    const featureLayerUnitsAndUsers: MapViewportUnitView[] = [];
    const featureLayerUnitsAndUsersById = useRef<Record<number, MapViewportUnitView>>({});
    const [clusterUnitAndUserIds, setClusterUnitAndUserIds] = useState<number[]>([]);

    // get units and users from units. push all units and users to `featureLayerUnitsAndUsers`. unitFeatureLayer will renderer all unit/user in `featureLayerUnitsAndUsers`
    if (mapViewportUnits && mapViewportUnits.length) {
        for (let i = 0; i < mapViewportUnits.length; i++) {
            const mapUnit = mapViewportUnits[i];

            if (showMobileDevicesOnMap && mapUnit?.visible && mapUnit.unitUserLocations?.length) {
                for (let j = 0; j < mapUnit.unitUserLocations.length; j++) {
                    const unitUserLocation = mapUnit.unitUserLocations[j];
                    if (unitUserLocation.entityId) {
                        const unitUserGpsLocation = getUnitUserLocation(unitUserLocation);
                        if (
                            !!unitUserGpsLocation &&
                            unitUserGpsLocation.entityId &&
                            isValidLatLng(
                                unitUserGpsLocation.latitude,
                                unitUserGpsLocation.longitude
                            )
                        ) {
                            const user: MapViewportUnitView = {
                                ...mapUnit,
                                // 'User' will have same fields as 'Unit', except for following fields updated for 'User' entity type.
                                // TODO: MAP RND-11325
                                visible: true,
                                entityId: unitUserGpsLocation.entityId,
                                type: EsriMapEntityTypeEnum.USER,
                                // copy user location potentially inaccurate to MapViewportUnitView.isLocationPotentiallyInaccurate
                                isLocationPotentiallyInaccurate:
                                    unitUserLocation.isLocationPotentiallyInaccurate,
                                // copy user gpsLocation to MapViewportUnitView.gpsLocation
                                gpsLocation: unitUserGpsLocation,
                                // user will not have geoLocation
                                geoLocation: undefined,
                                // user will not have any unitUserLocations
                                unitUserLocations: [],
                                personnelName:
                                    unitUserLocation.userNameDisplay ?? mapUnit.personnelName,
                            };
                            featureLayerUnitsAndUsers.push(user);
                            featureLayerUnitsAndUsersById.current[
                                unitUserGpsLocation.entityId
                            ] = user;
                        }
                    }
                }
            }

            const unitLocation = getMapViewportUnitLocation(
                mapUnit,
                showUnitLocationWhenGpsIsNotAvailable
            );
            if (
                mapUnit?.visible &&
                !!unitLocation &&
                isValidLatLng(unitLocation.latitude, unitLocation.longitude)
            ) {
                const unit: MapViewportUnitView = {
                    ...mapUnit,
                    type: EsriMapEntityTypeEnum.UNIT,
                };
                featureLayerUnitsAndUsers.push(unit);
                featureLayerUnitsAndUsersById.current[unit.entityId] = unit;
            }
        }
    }

    const [unitsLayer, setUnitsLayer] = useState<FeatureLayer>();
    const mapIconTheme = useMapIconTheme(getMapIconColorMode(mapMode));
    const layerMapIconThemeRef = useRef(mapIconTheme);
    const prevMapIconTheme = usePreviousLayer(mapIconTheme, unitsLayer);
    const prevSelectedUnitId = usePreviousLayer(selectedUnitId, unitsLayer);
    const prevSelectedUnitUserId = usePreviousLayer(selectedUnitUserId, unitsLayer);
    const prevSelectedMapLedgerEntityType = usePreviousLayer(
        selectedMapLedgerEntityType,
        unitsLayer
    );

    // unit needs to be updated:
    // if selectedMapLedgerEntityType changed from UNIT or to UNIT
    // if unit lat/lng changed
    // if unit gps status changed
    // if selectedUnitId differs from prevSelectedUnitId and entityId is equal to either
    // if unit status change
    // if the map icon theme changed, since icon names depend on it
    const didUnitGraphicChange = useCallback(
        (currUnit: MapViewportUnitView, prevUnit: MapViewportUnitView) => {
            const prevUnitLocation = getMapViewportUnitLocation(
                prevUnit,
                showUnitLocationWhenGpsIsNotAvailable
            );
            const currUnitLocation = getMapViewportUnitLocation(
                currUnit,
                showUnitLocationWhenGpsIsNotAvailable
            );
            const isEntityTypeChanged =
                prevSelectedMapLedgerEntityType !== selectedMapLedgerEntityType &&
                (prevSelectedMapLedgerEntityType === EsriMapEntityTypeEnum.UNIT ||
                    selectedMapLedgerEntityType === EsriMapEntityTypeEnum.UNIT);
            const isUnitStatusChanged =
                currUnit.unitStateGroup !== prevUnit.unitStateGroup ||
                getUnitStateId(currUnit) !== getUnitStateId(prevUnit) ||
                currUnit.summaryCategory !== prevUnit.summaryCategory;
            return (
                mapIconTheme !== prevMapIconTheme ||
                isEntityTypeChanged ||
                isUnitStatusChanged ||
                !areLatLngsEqual(prevUnitLocation, currUnitLocation) ||
                prevUnit.isLocationPotentiallyInaccurate !==
                    currUnit.isLocationPotentiallyInaccurate ||
                (selectedUnitId !== prevSelectedUnitId &&
                    (currUnit.entityId === selectedUnitId ||
                        currUnit.entityId === prevSelectedUnitId))
            );
        },
        [
            mapIconTheme,
            prevMapIconTheme,
            prevSelectedUnitId,
            selectedUnitId,
            prevSelectedMapLedgerEntityType,
            selectedMapLedgerEntityType,
            showUnitLocationWhenGpsIsNotAvailable,
        ]
    );

    const createUnitGraphics = useCallback(
        (mapViewportUnits: MapViewportUnitView[]) => {
            const unitGraphics: Graphic[] = [];
            let selectedGraphic: Graphic | undefined;

            for (const mapViewportUnit of mapViewportUnits) {
                const objectId = entityIdToObjectIdMapCacheRef.current.get(
                    mapViewportUnit.entityId
                );
                const unitGraphic = createUnitGraphic({
                    objectId,
                    mapViewportUnit,
                    selectedUnitId,
                    selectedUnitUserId,
                    selectedMapLedgerEntityType,
                    showUnitLocationWhenGpsIsNotAvailable,
                });
                if (unitGraphic) {
                    if (mapViewportUnit.entityId === selectedUnitId) {
                        selectedGraphic = unitGraphic;
                    } else {
                        unitGraphics.push(unitGraphic);
                    }
                }
            }
            if (selectedGraphic) {
                unitGraphics.push(selectedGraphic);
            }
            return unitGraphics;
        },
        [
            selectedUnitId,
            selectedUnitUserId,
            selectedMapLedgerEntityType,
            showUnitLocationWhenGpsIsNotAvailable,
        ]
    );

    const setPopupContentInfo = useCallback(
        (unitOrUserIds: number[]) => {
            const popupContainer = document.createElement('div');

            // add unit rows to the content container
            unitOrUserIds.map((unitOrUserId) => {
                const unitOrUser = featureLayerUnitsAndUsersById.current[unitOrUserId];

                if (unitOrUser) {
                    const { iconName } = getUnitIconNameAndUiState({
                        mapViewportUnit: unitOrUser,
                        selectedUnitId,
                        selectedMapLedgerEntityType,
                    });
                    const { row, icon, label } = createClusterContentInfoRow({
                        labelInnerHTML: `${unitOrUser.callSign}${
                            unitOrUser.priority ? ` (${unitOrUser.priority})` : ''
                        }`,
                    });
                    const isUser = unitOrUser.type === 'USER';
                    icon.src = getMapIconTheme().getUnitIconUrl(iconName);
                    if (!icon.src) {
                        icon.src = isUser
                            ? policeOnFootOnlineInactive
                            : agencyDefaultUnitIcon[
                                  unitOrUser.agencyTypeGlobalAttrId as AgencyTypeGlobal
                              ];
                    }
                    if (selectedUnitUserId && unitOrUserId === selectedUnitUserId) {
                        // user is selected, highlight user
                        label.style.color = getClusterPopupSelectedLabelColor();
                    } else if (
                        !selectedUnitUserId &&
                        selectedUnitId &&
                        unitOrUserId === selectedUnitId
                    ) {
                        // unit is selected, highlight unit
                        label.style.color = getClusterPopupSelectedLabelColor();
                    }

                    row.appendChild(icon);
                    row.appendChild(label);

                    const selectUnitUserOrUnit = () => {
                        if (isUser) {
                            selectUnitUser?.(unitOrUser.entityId);
                        } else {
                            selectUnit?.(unitOrUser.entityId);
                        }
                    };

                    row.onclick = () => {
                        selectUnitUserOrUnit();
                    };

                    row.oncontextmenu = (event) => {
                        event.preventDefault();
                        if (!isUser && dispatchFromMap && !isFirstResponder) {
                            openUnitContextMenu(unitOrUser.entityId);
                        } else {
                            selectUnitUserOrUnit();
                        }
                    };

                    // add row to popup content
                    popupContainer.appendChild(row);
                }
            });

            return popupContainer;
        },
        [
            dispatchFromMap,
            isFirstResponder,
            openUnitContextMenu,
            selectUnit,
            selectUnitUser,
            selectedMapLedgerEntityType,
            selectedUnitId,
            selectedUnitUserId,
        ]
    );

    const layerView = useRef<FeatureLayerView | undefined>(undefined);
    // logic to handle populating the cluster popup once selected
    const openUnitsClusterPopup = useCallback(
        async (popupLocation: Point, clusterGraphic: Graphic) => {
            if (mapView && unitsLayer) {
                // add listener for removing className and other unit cluster specific rules when popup is closed
                reactiveUtils.watch(
                    () => mapView.popup.visible,
                    (isVisible) => {
                        // if closed
                        if (
                            !isVisible &&
                            mapView.popup.title === FEATURE_LAYERS_PROPERTIES.UNITS.title
                        ) {
                            const popupElements = document.getElementsByClassName(
                                FEATURE_LAYERS_PROPERTIES.UNITS.clusterPopupClassName
                            );
                            const popup = popupElements[0];
                            if (!popup) {
                                return;
                            }
                            if (
                                popup.classList.contains(
                                    FEATURE_LAYERS_PROPERTIES.UNITS.clusterPopupClassName
                                )
                            ) {
                                popup.classList.remove(
                                    FEATURE_LAYERS_PROPERTIES.UNITS.clusterPopupClassName
                                );
                            }
                        }
                    }
                );

                // get features from layer view to add to popup content
                unitsLayer.when().then(async () => {
                    layerView.current = await mapView.whenLayerView(unitsLayer);
                    if (layerView.current) {
                        const query = layerView.current.createQuery();
                        query.aggregateIds = [clusterGraphic.getObjectId()];
                        // get unit features/graphics from selected cluster
                        const { features } = await layerView.current.queryFeatures(query);
                        const clusterUnitAndUserIds = features.map(
                            (graphic) => graphic.attributes.entityId
                        );
                        const clusterPopupContent = setPopupContentInfo(clusterUnitAndUserIds);
                        setClusterUnitAndUserIds(clusterUnitAndUserIds);
                        // set initial popup content
                        mapView.popup.content = clusterPopupContent;

                        // Add className to popup.
                        const popup = (mapView.popup as CustomPopup).domNode;

                        if (!popup) {
                            return;
                        }

                        if (
                            !popup.classList.contains(
                                FEATURE_LAYERS_PROPERTIES.UNITS.clusterPopupClassName
                            )
                        ) {
                            popup.classList.add(
                                FEATURE_LAYERS_PROPERTIES.UNITS.clusterPopupClassName
                            );
                        }

                        // Set popup rules specific to unit cluster.
                        // place popup at location relative to cluster icon
                        mapView.popup.alignment = 'top-center';
                        // hide collapse button in popup
                        mapView.popup.visibleElements.collapseButton = false;
                        // hide default actions in popup
                        mapView.popup.viewModel.includeDefaultActions = false;

                        // Open popup once everything is set.
                        mapView.popup.open({
                            title: FEATURE_LAYERS_PROPERTIES.UNITS.title,
                            location: popupLocation,
                        });
                    }
                });
            }
        },
        [mapView, unitsLayer, setPopupContentInfo]
    );

    useEffect(
        function updatePopupContentInfo() {
            // rerender popup content to update selected unit/user, if popup is open
            if (enableUnitClusteringOnMap && mapView?.popup.visible) {
                if (
                    prevSelectedUnitId !== selectedUnitId ||
                    prevSelectedUnitUserId !== selectedUnitUserId
                ) {
                    mapView.popup.content = setPopupContentInfo(clusterUnitAndUserIds);
                }
            }
        },
        [
            mapView?.popup,
            prevSelectedUnitId,
            selectedUnitId,
            prevSelectedUnitUserId,
            selectedUnitUserId,
            clusterUnitAndUserIds,
            setPopupContentInfo,
            enableUnitClusteringOnMap,
        ]
    );

    // handles initializing empty feature layer
    useEffect(
        function addUnitFeatureLayerToWebMapLayers() {
            // need modules to load
            if (webMap) {
                const unitFeatureLayer = createUnitsFeatureLayer(
                    enableUnitClusteringOnMap,
                    clusterRadius,
                    layerMapIconThemeRef.current
                );
                setUnitsLayer(unitFeatureLayer);
                webMap.layers.add(unitFeatureLayer);
            }
        },
        [webMap, enableUnitClusteringOnMap, clusterRadius]
    );

    useEffect(
        function matchUnitIconsToMapIconTheme() {
            if (!unitsLayer || layerMapIconThemeRef.current === mapIconTheme) {
                return;
            }
            applyUnitIconTheme(unitsLayer, mapIconTheme);
            layerMapIconThemeRef.current = mapIconTheme;
        },
        [unitsLayer, mapIconTheme]
    );

    useLayerControl<MapViewportUnitView>({
        layer: unitsLayer,
        currItems: featureLayerUnitsAndUsers,
        didItemChange: didUnitGraphicChange,
        createGraphics: createUnitGraphics,
        entityIdToObjectIdMapCacheRef,
    });

    return { unitsLayer, openUnitsClusterPopup };
};
